import React from "react";
import * as Mui from "@mui/material";
import {
  ArrowForwardRounded,
  LoginRounded,
  LogoutRounded,
  PersonOffRounded,
  ScheduleRounded,
} from "@mui/icons-material";
import { designColors } from "services/constants/designTokens";
import { ctaButtonSx, sectionCardSx, SectionHeader } from "./homeUi";

const buildAttendanceExceptionItems = (counts) => [
  {
    label: "Check In",
    value: counts?.present_count ?? 0,
    icon: LoginRounded,
    color: "#16A34A",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=present",
    },
  },
  {
    label: "Early Check-out",
    value: counts?.early_exit_count ?? 0,
    icon: LogoutRounded,
    color: "#F97316",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=present&presentStatus=early",
    },
  },
  {
    label: "Absent",
    value: counts?.absent_count ?? 0,
    icon: PersonOffRounded,
    color: "#EF4444",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=absent",
    },
  },
  {
    label: "Late Check-in",
    value: counts?.late_count ?? 0,
    icon: ScheduleRounded,
    color: "#F97316",
    navigationTarget: {
      moduleName: "Attendance",
      search: "component=employee-view&attendance=present&presentStatus=late",
    },
  },
];

const attendanceEmployeeViewSearch = "component=employee-view";

export const AttendanceExceptionsSection = ({
  navigateToModule,
  attendanceCounts,
  dateSuffix = "Today",
  loading = false,
}) => {
  const attendanceExceptionItems =
    buildAttendanceExceptionItems(attendanceCounts);

  return (
    <Mui.Box
      sx={{
        ...sectionCardSx,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {!loading && (
        <SectionHeader
          title={`Attendance Exceptions (${dateSuffix})`}
          tooltip="Team members who checked in, checked out early, were absent, or checked in late for the selected day."
          actionLabel="View all"
          onAction={() =>
            navigateToModule("Attendance", attendanceEmployeeViewSearch)
          }
        />
      )}
      <Mui.Stack spacing={0.5} sx={{ flex: 1, pt: 1.25 }}>
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <Mui.Stack
                key={index}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.25}
                sx={{ py: 0.9 }}
              >
                <Mui.Stack direction="row" spacing={1.25} alignItems="center">
                  <Mui.Skeleton
                    variant="rounded"
                    width={34}
                    height={34}
                    sx={{ borderRadius: 1.5, flexShrink: 0 }}
                  />
                  <Mui.Skeleton variant="text" width={120} height={22} />
                </Mui.Stack>
                <Mui.Stack direction="row" spacing={0.25} alignItems="center">
                  <Mui.Skeleton variant="text" width={18} height={24} />
                  <Mui.Skeleton variant="circular" width={18} height={18} />
                </Mui.Stack>
              </Mui.Stack>
            ))
          : attendanceExceptionItems.map((item) => (
              <Mui.Stack
                key={item.label}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1.25}
                onClick={() =>
                  navigateToModule(
                    item.navigationTarget.moduleName,
                    item.navigationTarget.search,
                  )
                }
                sx={{
                  py: 0.9,
                  borderBottom: `1px solid ${designColors.slate100}`,
                  cursor: "pointer",
                  borderRadius: 1.5,
                  transition: "background-color 120ms ease",
                  "&:hover": {
                    backgroundColor: designColors.slate50,
                  },
                }}
              >
                <Mui.Stack direction="row" spacing={1.25} alignItems="center">
                  <Mui.Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 1.5,
                      backgroundColor: `${item.color}12`,
                      color: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <item.icon sx={{ fontSize: 18 }} />
                  </Mui.Box>
                  <Mui.Typography
                    sx={{
                      fontSize: 14.5,
                      fontWeight: 500,
                      color: designColors.slate900,
                    }}
                  >
                    {item.label}
                  </Mui.Typography>
                </Mui.Stack>
                <Mui.Stack direction="row" spacing={0.25} alignItems="center">
                  <Mui.Typography
                    sx={{ fontSize: 16, fontWeight: 600, color: item.color }}
                  >
                    {item.value}
                  </Mui.Typography>
                  <ArrowForwardRounded
                    sx={{ fontSize: 18, color: designColors.slate400 }}
                  />
                </Mui.Stack>
              </Mui.Stack>
            ))}
      </Mui.Stack>
      {loading ? (
        <Mui.Skeleton
          variant="rounded"
          height={36}
          sx={{ borderRadius: 999, mt: 1.5 }}
        />
      ) : (
        <Mui.Button
          fullWidth
          endIcon={<ArrowForwardRounded />}
          sx={{ ...ctaButtonSx, mt: 1.5 }}
          onClick={() =>
            navigateToModule("Attendance", attendanceEmployeeViewSearch)
          }
        >
          View All Exceptions
        </Mui.Button>
      )}
    </Mui.Box>
  );
};
